import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import {
  useMySubmissions,
  useCreateSubmission,
} from "@/hooks/use-submissions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Send, X } from "lucide-react";

export const Route = createFileRoute("/_main/exams/$slug/submissions")({
  component: SubmissionsPage,
});

function SubmissionsPage() {
  const { slug: examSlug } = Route.useParams();
  const { loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const { data: allSubmissions = [], isLoading: loading } = useMySubmissions();
  const submissions = useMemo(
    () => allSubmissions.filter((s) => s.exam_slug === examSlug),
    [allSubmissions, examSlug],
  );
  const createSubmissionMutation = useCreateSubmission(examSlug);
  const submitting = createSubmissionMutation.isPending;
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [textKo, setTextKo] = useState("");
  const [explanation, setExplanation] = useState("");
  const [choices, setChoices] = useState([
    { label: "A", text: "", is_correct: false },
    { label: "B", text: "", is_correct: false },
    { label: "C", text: "", is_correct: false },
    { label: "D", text: "", is_correct: false },
  ]);

  async function handleSubmit() {
    if (!textKo.trim()) return;
    const correctCount = choices.filter((c) => c.is_correct).length;
    if (correctCount === 0) {
      alert(t("submissions.choices_label"));
      return;
    }

    try {
      await createSubmissionMutation.mutateAsync({
        text_ko: textKo,
        explanation,
        is_multiple: correctCount > 1,
        choices: choices.filter((c) => c.text.trim()),
      });
      setShowForm(false);
      setTextKo("");
      setExplanation("");
      setChoices([
        { label: "A", text: "", is_correct: false },
        { label: "B", text: "", is_correct: false },
        { label: "C", text: "", is_correct: false },
        { label: "D", text: "", is_correct: false },
      ]);
    } catch {
      alert(t("common.error"));
    }
  }

  function statusBadge(status: string) {
    switch (status) {
      case "pending":
        return <Badge variant="secondary">{t("submissions.pending")}</Badge>;
      case "approved":
        return <Badge variant="default">{t("submissions.approved")}</Badge>;
      case "rejected":
        return <Badge variant="destructive">{t("submissions.rejected")}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  }

  if (authLoading) return null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t("submissions.title")}</h1>
            <p className="mt-1 text-sm text-zinc-500">
              {t("submissions.desc")}
            </p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="cursor-pointer"
          >
            {showForm ? (
              <>
                <X className="mr-1 h-4 w-4" /> {t("common.cancel")}
              </>
            ) : (
              <>
                <Plus className="mr-1 h-4 w-4" /> {t("submissions.submit_question")}
              </>
            )}
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">{t("submissions.new_submission")}</CardTitle>
              <CardDescription>
                {t("submissions.new_submission_desc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("submissions.question_text")}
                </label>
                <textarea
                  value={textKo}
                  onChange={(e) => setTextKo(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  placeholder={t("submissions.question_placeholder")}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("submissions.choices_label")}
                </label>
                <div className="space-y-2">
                  {choices.map((c, i) => (
                    <div key={c.label} className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant={c.is_correct ? "default" : "outline"}
                        size="sm"
                        className="w-10 shrink-0 cursor-pointer"
                        onClick={() => {
                          const updated = [...choices];
                          updated[i].is_correct = !updated[i].is_correct;
                          setChoices(updated);
                        }}
                      >
                        {c.label}
                      </Button>
                      <Input
                        value={c.text}
                        onChange={(e) => {
                          const updated = [...choices];
                          updated[i].text = e.target.value;
                          setChoices(updated);
                        }}
                        placeholder={`${t("submissions.choice_placeholder")} ${c.label}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {t("submissions.explanation_optional")}
                </label>
                <textarea
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                  placeholder={t("submissions.explanation_placeholder")}
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting || !textKo.trim()}
                className="w-full cursor-pointer"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                {t("common.submit")}
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("submissions.my_submissions")}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : submissions.length === 0 ? (
              <p className="py-8 text-center text-sm text-zinc-400">
                {t("submissions.no_submissions")}
              </p>
            ) : (
              <div className="space-y-3">
                {submissions.map((sub) => (
                  <div
                    key={sub.id}
                    className="rounded-lg border border-zinc-100 p-4 dark:border-zinc-800"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="line-clamp-2 text-sm">{sub.text_ko}</p>
                      {statusBadge(sub.status)}
                    </div>
                    {sub.review_comment && (
                      <p className="mt-2 text-xs text-zinc-500">
                        {t("submissions.review")}: {sub.review_comment}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-zinc-400">
                      {new Date(sub.created_at).toLocaleDateString(locale === "en" ? "en-US" : "ko-KR")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
